// useTweaks — single source of truth for visual tweaks (theme, accent,
// density, typography, chat bubble style, etc.).
//
// Persists to localStorage so the user's choices stick across reloads.
// Later this can be backed by `/api/me/preferences` so settings sync
// across devices; the API surface stays the same either way.
//
// Replaces the design-handoff prototype's host-protocol implementation
// (postMessage __edit_mode_set_keys) — that was a design-tool affordance,
// not anything an app needs.

import { useCallback, useEffect, useState } from "preact/hooks";

export interface Tweaks {
  dark: boolean;
  accent: string;
  density: "compact" | "regular" | "comfy";
  type: "geist" | "editorial" | "mono";
  bubble: "rounded" | "square" | "minimal";
  sidebarDense: boolean;
  showToolCalls: boolean;
  debugTelemetry: boolean;
}

export const TWEAK_DEFAULTS: Tweaks = {
  dark: true,
  accent: "oklch(0.74 0.18 340)",
  density: "regular",
  type: "geist",
  bubble: "rounded",
  sidebarDense: false,
  showToolCalls: true,
  debugTelemetry: false,
};

const LS_KEY = "hub.tweaks.v1";

function load(): Tweaks {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    const parsed = JSON.parse(raw);
    // Merge so a new field added in a future version doesn't lose its default.
    return { ...TWEAK_DEFAULTS, ...parsed };
  } catch {
    return TWEAK_DEFAULTS;
  }
}

function save(t: Tweaks) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(t)); } catch {}
}

type SetTweak =
  & (<K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void)
  & ((edits: Partial<Tweaks>) => void);

export function useTweaks(): [Tweaks, SetTweak] {
  const [values, setValues] = useState<Tweaks>(load);

  const setTweak = useCallback<SetTweak>((keyOrEdits: any, val?: any) => {
    const edits = typeof keyOrEdits === "object" && keyOrEdits !== null
      ? keyOrEdits
      : { [keyOrEdits]: val };
    setValues((prev) => {
      const next = { ...prev, ...edits };
      save(next);
      return next;
    });
  }, []);

  // Apply tokens to <html> on every change. Centralized here so callers
  // don't have to remember to do it.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", values.dark ? "dark" : "light");
    root.setAttribute("data-density", values.density);
    root.style.setProperty("--accent", values.accent);
    root.style.setProperty(
      "--accent-ink",
      values.dark ? "oklch(0.16 0.04 280)" : "oklch(0.99 0.005 280)",
    );
    const pair = TYPE_PAIRINGS[values.type] || TYPE_PAIRINGS.geist;
    root.style.setProperty("--f-sans", pair.sans);
    root.style.setProperty("--f-serif", pair.serif);
    root.style.setProperty("--f-display", pair.display);
  }, [values.dark, values.density, values.accent, values.type]);

  return [values, setTweak];
}

const TYPE_PAIRINGS: Record<Tweaks["type"], { sans: string; serif: string; display: string }> = {
  geist:     { sans: '"Geist", system-ui, sans-serif',                serif: '"Instrument Serif", Georgia, serif', display: '"Bricolage Grotesque", "Geist", sans-serif' },
  editorial: { sans: '"Bricolage Grotesque", system-ui, sans-serif',  serif: '"Instrument Serif", Georgia, serif', display: '"Instrument Serif", Georgia, serif' },
  mono:      { sans: '"Geist", system-ui, sans-serif',                serif: '"Geist Mono", monospace',            display: '"Geist Mono", monospace' },
};

export const ACCENT_OPTIONS = [
  "oklch(0.74 0.18 340)", // magenta
  "oklch(0.78 0.16 30)",  // coral
  "oklch(0.82 0.16 75)",  // amber
  "oklch(0.84 0.16 145)", // lime
  "oklch(0.80 0.14 220)", // cyan
  "oklch(0.74 0.18 290)", // violet
];
